import get from 'lodash/get'
import {
  sessionConfig,
  specificRoles,
  configDocuments,
  collections,
  customer_kinds,
} from '../../../common/constants'

const state = {
  currentUser: null,
  currentCustomer: null,
  customers: [],
  isOmniscientAdmin: false,
  keycloakUser: null,
  currentPermissions: null,
  currentDefaultPage: null,
}

const getters = {
  currentUser: (state) => state.currentUser,
  customers: (state) => state.customers,
  currentCustomer: (state) => state.currentCustomer,
  currentIndex: (state) => get(state.currentCustomer, 'index', ''),
  currentCustomerName: (state) => get(state.currentCustomer, 'name', null),
  currentProfile: (state) =>
    state.isOmniscientAdmin
      ? 'admin'
      : get(state.currentCustomer, 'profiles[0]', null),
  isAdmin: (state) =>
    state.isOmniscientAdmin
      ? true
      : get(state.currentCustomer, 'profiles[0]', false) === 'admin',
  isSuperAdmin: (state) => Boolean(state.isOmniscientAdmin),
  isPreperer: (state) =>
    state.isOmniscientAdmin
      ? false
      : get(state.currentCustomer, 'profiles[0]', false) === 'preperer',
  isCustomerRenter: (state) =>
    get(state.currentCustomer, 'kind', false) === customer_kinds.RENTER,
  isCustomerWorksite: (state) =>
    get(state.currentCustomer, 'kind', false) === customer_kinds.WORKSITE,
  keycloakUser: (state) => state.keycloakUser,
  currentPermissions: (state) => state.currentPermissions,
  currentDefaultPage: (state) => state.currentDefaultPage,
}

const actions = {
  LOGIN: async ({ dispatch }, { kuzzle, strategy, user }) => {
    let jwt
    try {
      const response = await kuzzle.query(
        {
          controller: 'auth',
          action: 'login',
          strategy: strategy,
          cookieAuth: true,
          body: {
            access_token: user.access_token,
            id_token: user.id_token,
          },
        },
        { verb: 'POST' },
      )

      if (response.status !== 200) {
        throw response.error.message
      }
      jwt = response.result.jwt
      kuzzle.jwt = jwt
      await dispatch('FETCH_CURRENT_USER', kuzzle)
    } catch (error) {
      console.error(error)
      throw error
    }
  },
  CHECK_TOKEN: async ({ dispatch, getters }, kuzzle) => {
    const result = await kuzzle.auth.checkToken()
    if (result.kuid === '-1' || !result.valid) {
      await dispatch('LOG_OUT', kuzzle)
      return false
    }

    if (!getters.currentUser) {
      await dispatch('FETCH_CURRENT_USER', kuzzle)
    }

    return true
  },
  FETCH_CURRENT_USER: async ({ commit, dispatch }, kuzzle) => {
    let currentUser

    try {
      const user = await kuzzle.auth.getCurrentUser()
      currentUser = user
    } catch (error) {
      throw error
    }

    commit('SET_CURRENT_USER', currentUser)
    // Disable idle logout for screen users
    if (state.currentUser.content.profileIds.includes(specificRoles.screen)) {
      await kuzzle.query({
        controller: 'auth',
        action: 'refreshToken',
        expiresIn: sessionConfig.SCREEN_EXPIRE_TIME,
      })
    }

    let currentCustomer = null
    if (state.currentUser.content.profileIds.includes(specificRoles.admin)) {
      const customers = []
      try {
        let searchResult = await kuzzle.document.search(
          configDocuments.multiTenancyConfig,
          collections.CUSTOMERS,
          {},
          {
            scroll: '30s',
          },
        )
        while (searchResult) {
          searchResult.hits.forEach((hit) => {
            customers.push({
              ...hit._source,
              index: `customer-${hit._source.kind}-${hit._source.name}`,
            })
          })
          searchResult = await searchResult.next()
        }
      } catch (error) {
        throw error
      }

      commit('SET_IS_OMNISCIENT_ADMIN', true)
      commit(
        'SET_CUSTOMERS_LIST',
        customers.sort((a, b) => (a.name > b.name ? 1 : -1)),
      )

      // find persisted customer from local storage if any, otherwise fallback to first one in result list
      currentCustomer = findPersistedCustomerIfAny(customers) ?? customers[0]
      // TODO: share with non-admin ?
      commit('SET_CURRENT_CUSTOMER', currentCustomer)
    } else {
      commit('SET_IS_OMNISCIENT_ADMIN', false)
      const request = {
        controller: 'omniscient/user',
        action: 'listIndexes',
        body: {
          userId: currentUser._id,
        },
      }
      const customers = (await kuzzle.query(request)).result
      commit('SET_CUSTOMERS_LIST', customers, [])

      // find persisted customer from local storage if any, otherwise fallback to first one in result list
      currentCustomer = findPersistedCustomerIfAny(customers) ?? customers[0]
      commit('SET_CURRENT_CUSTOMER', currentCustomer)
    }

    const requestPermission = {
      controller: 'omniscient/permissions',
      action: 'search',
      body: {
        customer: currentCustomer.index,
      },
    }

    try {
      const { result } = await kuzzle.query(requestPermission)
      const { permissions, defaultPage } = result
      commit('SET_CURRENT_USER_PERMISSIONS', permissions)
      commit('SET_CURRENT_DEFAULT_PAGE', defaultPage)
    } catch (error) {
      throw error
    }

    await dispatch('site/FETCH_SITES', kuzzle, { root: true })
    await dispatch('referential/FETCH_CURRENT_REFERENTIAL', kuzzle, {
      root: true,
    })
    await dispatch('customerConfig/FETCH_CURRENT_CUSTOMER_CONFIG', kuzzle, {
      root: true,
    })
    dispatch('userPreferences/FETCH_USER_PREFERENCES', null, {
      root: true,
    })
  },
  LOG_OUT: async ({ commit }, kuzzle) => {
    /**
     * You should tear down your session here.
     */
    commit('UNSET_CURRENT_USER')
    commit('UNSET_CURRENT_CUSTOMER')
    commit('UNSET_CUSTOMERS_LIST')
    commit('site/SET_CURRENT_SITE', null, { root: true })
    commit('referential/SET_CURRENT_REFERENTIAL', null, { root: true })
    commit('customerConfig/SET_CURRENT_CUSTOMER_CONFIG', null, { root: true })
    commit('userPreferences/SET_USER_PREFERENCES', null, { root: true })

    try {
      await kuzzle.auth.logout()
      await kuzzle.disconnect()
      kuzzle.jwt = null
    } catch (e) {
      console.error(e)
    }
    localStorage.removeItem('current_customer_name')
  },
  KEYCLOAK_SIGNIN_CALLBACK: async ({ commit }, keycloak) => {
    const user = await keycloak.userManager.signinCallback(window.location.href)
    commit('SET_USER', user)
  },
  KEYCLOAK_SIGNIN: async ({ dispatch }, keycloak) => {
    await dispatch('KEYCLOAK_SIGNIN_REDIRECT', keycloak.userManager)
  },
  KEYCLOAK_SIGNIN_REDIRECT: async ({}, userManager) => {
    await userManager.signinRedirect()
  },
  FETCH_PERMISSION: async ({ commit, rootGetters }, kuzzle) => {
    if (!rootGetters['auth/currentIndex']) {
      return
    }

    const request = {
      controller: 'omniscient/permissions',
      action: 'search',
      body: {
        customer: rootGetters['auth/currentIndex'],
      },
    }
    try {
      const { result } = await kuzzle.query(request)
      const { permissions, defaultPage } = result
      commit('SET_CURRENT_USER_PERMISSIONS', permissions)
      commit('SET_CURRENT_DEFAULT_PAGE', defaultPage)
    } catch (error) {
      throw error
    }
  },
}

const mutations = {
  SET_CURRENT_USER: (state, user) => {
    state.currentUser = user
  },
  UNSET_CURRENT_USER: (state) => {
    state.currentUser = null
  },
  SET_CURRENT_CUSTOMER: (state, customer) => {
    state.currentCustomer = customer
  },
  SET_IS_OMNISCIENT_ADMIN: (state, isOmniscientAdmin) => {
    state.isOmniscientAdmin = isOmniscientAdmin
  },
  UNSET_CURRENT_CUSTOMER: (state) => {
    state.currentCustomer = null
  },
  SET_CUSTOMERS_LIST: (state, customers) => {
    state.customers = customers
  },
  UNSET_CUSTOMERS_LIST: (state) => {
    state.customers = []
  },
  SET_USER: (state, user) => {
    state.keycloakUser = user
  },
  SET_CURRENT_USER_PERMISSIONS: (state, permissions) => {
    state.currentPermissions = permissions
  },
  SET_CURRENT_DEFAULT_PAGE: (state, page) => {
    state.currentDefaultPage = page
  },
}

function findPersistedCustomerIfAny(customers) {
  const persistedCustomerName = localStorage.getItem('current_customer_name')

  let foundCustomer = null
  if (persistedCustomerName && customers.length) {
    foundCustomer = customers.find((el) => el.name === persistedCustomerName)
  }

  return foundCustomer
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
